import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import { Separator } from "@vaiu/ui/separator";
import { 
  Palette, 
  Building, 
  CreditCard, 
  Wallet, 
  Star,
  ArrowRight,
  CheckCircle,
  Globe
} from 'lucide-react';
import { useBrandThemeContext } from '@/context/BrandThemeContext';
// import BrandLogo from '@/components/shared/BrandLogo';
import BrandThemeSelector from '@/components/BrandThemeSelector';

const BrandThemeDemo = () => {
  const { currentBrand, theme } = useBrandThemeContext();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            {/* <BrandLogo size="xl" variant="default" /> */}
          </div>
          <div>
            <h1 className="text-4xl font-bold text-foreground">
              Brand Theme Demo
            </h1>
            <p className="text-xl text-muted-foreground mt-2">
              Experience how the platform adapts to different brand identities
            </p>
          </div>
        </div>

        {/* Brand Theme Selector */}
        <BrandThemeSelector />

        {/* Demo Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Banking Card */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Building className="w-6 h-6 text-primary" />
              </div>
              <CardTitle>Digital Banking</CardTitle>
              <CardDescription>
                Secure and modern banking solutions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Account Balance</span>
                <Badge variant="default">Active</Badge>
              </div>
              <div className="text-2xl font-bold text-primary">
                €1,234.56
              </div>
              <Button className="w-full">
                <Wallet className="w-4 h-4 mr-2" />
                Manage Account
              </Button>
            </CardContent>
          </Card>

          {/* Payment Card */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <CreditCard className="w-6 h-6 text-secondary" />
              </div>
              <CardTitle>Payment Solutions</CardTitle>
              <CardDescription>
                Fast and secure payment processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Processing Fee</span>
                  <span className="text-sm font-medium">2.9%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Settlement</span>
                  <span className="text-sm font-medium">Next Day</span>
                </div>
              </div>
              <Button variant="outline" className="w-full">
                <ArrowRight className="w-4 h-4 mr-2" />
                Process Payment
              </Button>
            </CardContent>
          </Card>

          {/* Features Card */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Star className="w-6 h-6 text-accent" />
              </div>
              <CardTitle>Premium Features</CardTitle>
              <CardDescription>
                Advanced tools for business growth
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span className="text-sm">Analytics Dashboard</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span className="text-sm">API Integration</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span className="text-sm">24/7 Support</span>
                </div>
              </div>
              <Button variant="secondary" className="w-full">
                <Globe className="w-4 h-4 mr-2" />
                Explore Features
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Theme Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Palette className="w-5 h-5" />
              <span>Current Theme Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* Brand Details */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Brand Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name:</span>
                    <span className="font-medium">{theme.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Company:</span>
                    <span className="font-medium">{theme.companyName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Brand Key:</span>
                    <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{currentBrand}</span>
                  </div>
                </div>
              </div>

              {/* Color Palette */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Color Palette</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(theme.brandColors).map(([key, color]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <div 
                        className="w-4 h-4 rounded border border-gray-300"
                        style={{ backgroundColor: color }}
                      />
                      <div className="text-xs">
                        <div className="font-medium capitalize">{key}</div>
                        <div className="text-muted-foreground font-mono">{color}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Usage Instructions */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Usage</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">URL Parameter:</span>
                    <div className="mt-1 p-2 bg-muted rounded text-xs font-mono">
                      ?brand={currentBrand}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">CSS Class:</span>
                    <div className="mt-1 p-2 bg-muted rounded text-xs font-mono">
                      .brand-{currentBrand}
                    </div>
                  </div>
                  <div className="text-muted-foreground">
                    The theme automatically applies when the brand parameter is detected in the URL.
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Brand Showcase */}
        <Card>
          <CardHeader>
            <CardTitle>Brand Showcase</CardTitle>
            <CardDescription>
              See how different brands appear with their unique styling
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {['vaiu', 'rbko', 'bnp', 'payformyfood'].map((brandKey) => (
                <div key={brandKey} className="text-center space-y-2">
                  <div className="p-4 border rounded-lg bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center"
                         style={{ 
                           backgroundColor: brandKey === 'rbko' ? '#FFD700' : 
                                          brandKey === 'bnp' ? '#00A651' : 
                                          brandKey === 'payformyfood' ? '#DC2626' : '#3B82F6',
                           color: brandKey === 'rbko' ? '#1B365D' : 'white'
                         }}>
                      <Building className="w-8 h-8" />
                    </div>
                    <h4 className="font-semibold capitalize">{brandKey}</h4>
                    <p className="text-xs text-muted-foreground">
                      {brandKey === 'rbko' ? 'Banking' : 
                       brandKey === 'bnp' ? 'Corporate' : 
                       brandKey === 'payformyfood' ? 'Restaurant' : 'Digital'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            This demo showcases the dynamic brand theming system. 
            Try different brands by changing the URL parameter or using the selector above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BrandThemeDemo; 